import { withTimeout } from 'shared/utils/withTimeout';
import { useLevelsStore } from 'store/level';
import { useProcessStore } from 'store/process';
import { useSitesStore } from 'store/site';

/** How long a table view waits for the dictionaries an issue is shown with. */
export const ISSUE_DICTIONARIES_TIMEOUT_MS = 30_000;

/** The collections an issue of the table view is mapped with: each tells when a complete list arrived. */
const ISSUE_DICTIONARY_STORES = [useSitesStore, useLevelsStore, useProcessStore];

/**
 * Whether the sites, levels and processes arrived: an issue of the table view
 * is mapped with them, and an issue read before them has none to be mapped to.
 */
export function areIssueDictionariesReady(): boolean {
  return ISSUE_DICTIONARY_STORES.every((store) => store.getState().isReady);
}

/** Calls `onChange` on every change of the dictionaries; returns the unsubscribe. */
export function subscribeToIssueDictionaries(onChange: () => void): () => void {
  const unsubscribes = ISSUE_DICTIONARY_STORES.map((store) => store.subscribe(onChange));
  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

/**
 * Resolves once the dictionaries arrived (at once when they have); rejects
 * after `timeoutMs`, so a report does not wait for ever for a failed load.
 */
export function waitForIssueDictionaries(timeoutMs = ISSUE_DICTIONARIES_TIMEOUT_MS): Promise<void> {
  let unsubscribe: () => void = () => undefined;
  const arrived = new Promise<void>((resolve) => {
    const resolveWhenReady = (): void => {
      if (areIssueDictionariesReady()) {
        resolve();
      }
    };
    unsubscribe = subscribeToIssueDictionaries(resolveWhenReady);
    resolveWhenReady();
  });
  return withTimeout(arrived, timeoutMs, () => {
    throw new Error('The sites, levels and processes did not load in time');
  }).finally(unsubscribe);
}
